import React, { useState, useMemo } from 'react'
import { Table, DatePicker, Button } from 'antd'
import styled from '@emotion/styled'
import moment from 'moment'
import { useQuery } from '@apollo/client'
import { GET_REF_STATUS } from 'Queries'

const dateFormat = 'YYYY/MM/DD'

export const FamiliesStatus = ({
  codDistribuidor,
  codObjetivo,
  year,
  fechaDesde,
  fechaHasta,
  isMensual
}) => {
  const objectiveStart = moment(fechaDesde, 'x').utc().startOf('day')
  const objectiveEnd = moment(fechaHasta, 'x').utc().endOf('day')
  
  // Ajuste: si hoy cae dentro del objetivo, filtrar desde día 1 de este mes hasta hoy
  const today = moment().utc()
  const todayInObjective = isMensual? today.isSameOrAfter(objectiveStart) && today.isSameOrBefore(objectiveEnd) : false
  
  const effectiveStart = todayInObjective ? today.clone().startOf('month') : objectiveStart
  const effectiveEnd = todayInObjective ? today : objectiveEnd
  
  // Estados iniciales
  const initialPeriod = [effectiveStart, effectiveEnd]
  const [period, setPeriod] = useState(initialPeriod)

  // Carga de datos del status
  const { data, loading, error } = useQuery(GET_REF_STATUS, {
    skip: !codDistribuidor || !codObjetivo || !year,
    variables: {
      codDistribuidor,
      codObjetivo,
      ano: year,
      fromDate: period[0]?.format('YYYY-MM-DD'),
      toDate: period[1]?.format('YYYY-MM-DD')
    }
  })



  // Función para resetear filtros
  const handleResetFilters = () => {
    setPeriod(initialPeriod)
  }

  // Verificar si hay filtros activos
  const hasActiveFilters = period && (
    period[0]?.valueOf() !== initialPeriod[0]?.valueOf() || 
    period[1]?.valueOf() !== initialPeriod[1]?.valueOf()
  )

  // Generar datos de la tabla
  const tableData = useMemo(() => {
    const rows = (data?.refStatus?.statusData || []).map(item => ({
      key: `fam-${item.numFamilias}`,
      numFamilias: item.numFamilias,
      numClientes: item.numClientes,
      impactos: item.numFamilias * item.numClientes,
      numClientesAA: item.numClientesAA,
      impactosAA: item.numFamilias * item.numClientesAA,
      isTotal: false,
      isTotalObjetivo: false
    }))

    // Calcular totales
    const totalImpactos = rows.reduce((sum, row) => sum + row.impactos, 0)
    const totalImpactosAA = rows.reduce((sum, row) => sum + row.impactosAA, 0)

    // Agregar fila de totales
    rows.push({
      key: 'total',
      numFamilias: 'Total impactos',
      numClientes: '-',
      impactos: totalImpactos,
      numClientesAA: '-',
      impactosAA: totalImpactosAA,
      isTotal: true,
      isTotalObjetivo: false
    })

    // Agregar fila de total impactos objetivo
    rows.push({
      key: 'total-objetivo',
      numFamilias: 'Total impactos objetivo',
      numClientes: '-',
      impactos: data?.refStatus?.totImpactosObjetivo,
      numClientesAA: '-',
      impactosAA: '-',
      isTotal: false,
      isTotalObjetivo: true
    })

    return rows
  }, [data?.refStatus?.statusData, data?.refStatus?.totImpactosObjetivo])

  // Definición de columnas
  const columns = [
    {
      title: 'Nº familias',
      dataIndex: 'numFamilias',
      key: 'numFamilias',
      width: 150,
      align: 'center',
      render: (value, record) => {
        const displayValue = (record.isTotal || record.isTotalObjetivo) ? value : `${value} fam`
        const obj = {
          children: <span style={{ fontWeight: 'bold' }}>{displayValue}</span>,
          props: { 
            style: { 
              background: (record.isTotal || record.isTotalObjetivo) ? '#F5F5F5' : '#D4EFDF',
              fontWeight: 'bold'
            } 
          },
        }
        return obj
      },
    },
    {
      title: 'Nº clientes',
      dataIndex: 'numClientes',
      key: 'numClientes',
      width: 130,
      align: 'center',
      render: (value, record) => ({
        children: <span style={{ fontWeight: (record.isTotal || record.isTotalObjetivo) ? 'bold' : 'normal' }}>{value}</span>,
        props: { 
          style: { 
            background: (record.isTotal || record.isTotalObjetivo) ? '#FFFFFF' : '#F3FAF3',
            fontWeight: (record.isTotal || record.isTotalObjetivo) ? 'bold' : 'normal'
          } 
        },
      }),
    },
    {
      title: 'Impactos',
      dataIndex: 'impactos',
      key: 'impactos',
      width: 130,
      align: 'center',
      render: (value, record) => ({
        children: <span style={{ fontWeight: (record.isTotal || record.isTotalObjetivo) ? 'bold' : 'normal' }}>{value}</span>,
        props: { 
          style: { 
            background: (record.isTotal || record.isTotalObjetivo) ? '#FFFFFF' : '#F3FAF3',
            fontWeight: (record.isTotal || record.isTotalObjetivo) ? 'bold' : 'normal'
          } 
        },
      }),
    },
    {
      title: 'Nº clientes AA',
      dataIndex: 'numClientesAA',
      key: 'numClientesAA',
      width: 150,
      align: 'center',
      render: (value, record) => ({
        children: <span style={{ fontWeight: (record.isTotal || record.isTotalObjetivo) ? 'bold' : 'normal' }}>{value}</span>,
        props: { 
          style: { 
            background: (record.isTotal || record.isTotalObjetivo) ? '#FFFFFF' : '#F3FAF3',
            fontWeight: (record.isTotal || record.isTotalObjetivo) ? 'bold' : 'normal'
          } 
        },
      }),
    },
    {
      title: 'Impactos AA',
      dataIndex: 'impactosAA',
      key: 'impactosAA',
      width: 130,
      align: 'center',
      render: (value, record) => ({
        children: <span style={{ fontWeight: (record.isTotal || record.isTotalObjetivo) ? 'bold' : 'normal' }}>{value}</span>,
        props: { 
          style: { 
            background: (record.isTotal || record.isTotalObjetivo) ? '#FFFFFF' : '#F3FAF3',
            fontWeight: (record.isTotal || record.isTotalObjetivo) ? 'bold' : 'normal'
          } 
        },
      }),
    },
  ]

  // Función de exportación a CSV
  const handleExport = () => {
    const headers = [
      'Nº familias',
      'Nº clientes',
      'Impactos',
      'Nº clientes AA',
      'Impactos AA'
    ]
    
    const csvContent = [
      headers.join(';'),
      ...tableData.map(row => [
        (row.isTotal || row.isTotalObjetivo) ? row.numFamilias : `${row.numFamilias} fam`,
        row.numClientes,
        row.impactos,
        row.numClientesAA,
        row.impactosAA
      ].join(';'))
    ].join('\n')

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `familias_status_${moment().format('YYYYMMDD')}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <Container>
      <ControlsWrapper>
        <LeftControls>
          <Button
            type="primary"
            onClick={handleExport}
            size="large"
          >
            Descargar CSV
          </Button>
        </LeftControls>
        <RightControls>
          <DatePicker.RangePicker
            allowClear={false}
            disabledDate={current => {
              return current && (current < objectiveStart || objectiveEnd < current)
            }}
            onChange={selection => {
              if (selection && selection.length > 1) {
                const [start, end] = selection
                // Si la fecha final es menor que la inicial, invertir
                setPeriod(start <= end ? selection : [end, start])
              }
            }}
            size="small"
            value={period}
            format={dateFormat}
            style={{ width: '240px' }}
          />
          <Button
            disabled={!hasActiveFilters}
            type="link"
            size="large"
            onClick={handleResetFilters}
          >
            Resetear filtros
          </Button>
        </RightControls>
      </ControlsWrapper>

      <TableWrapper>
        <StyledTable
          dataSource={tableData}
          columns={columns}
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'No hay datos disponibles' }}
        />
      </TableWrapper>
    </Container>
  )
}

const Container = styled.div`
  padding: 0;
`

const ControlsWrapper = styled.div`
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-width: 100%;
  gap: 10px;
`

const LeftControls = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

const RightControls = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  > * {
    margin: 0 5px;
  }
`

const TableWrapper = styled.div`
  background: transparent;
  box-shadow: none;
  margin-bottom: 5px;
  
  & .ant-table {
    background: white;
    box-shadow: 0 4px 12px 0 rgba(200, 198, 198, 0.5);
  }
`

const StyledTable = styled(Table)`
  & .ant-table-thead > tr > th {
    font-size: 14px;
    font-weight: 700;
    background: #FFFFFF !important;
    color: #205527 !important;
    padding: 8px 8px !important;
    text-align: center;
  }
  & .ant-table-tbody > tr > td {
    border-top: 1px solid darkGray;
    border-bottom: none;
  }
`
